import { apiClient, request } from '@/lib/apiClient';
import { announce, markSignedIn } from '@/lib/authSession';
import { tokenStore } from '@/lib/tokenStore';

export const authApi = {
  /** POST /auth/login -> { access_token, refresh_token, expires_in, user } */
  async login({ email, password }) {
    const tokens = await request({
      url: '/auth/login',
      method: 'POST',
      data: { email, password },
      // A failed sign-in must not trigger the refresh interceptor: there is no
      // session to refresh, and retrying would just repeat the 401.
      skipAuthRefresh: true,
    });
    tokenStore.setTokens(tokens);
    markSignedIn();
    // Other tabs may still show whoever was signed in before.
    announce('signed-in');
    return tokens;
  },

  /** POST /auth/register -> the new CUSTOMER user */
  register({ email, password, fullName, phone }) {
    return request({
      url: '/auth/register',
      method: 'POST',
      data: { email, password, full_name: fullName, phone: phone || null },
      skipAuthRefresh: true,
    });
  },

  /** POST /auth/register-admin -> new ADMIN user, shop & 1-month trial */
  async registerAdmin({ storeName, slug, fullName, email, password, phone }) {
    const res = await request({
      url: '/auth/register-admin',
      method: 'POST',
      data: {
        store_name: storeName,
        slug: slug || null,
        full_name: fullName,
        email,
        password,
        phone: phone || null,
      },
      skipAuthRefresh: true,
    });
    if (res.tokens) {
      tokenStore.setTokens(res.tokens);
      markSignedIn();
      announce('signed-in');
    }
    return res;
  },

  /** GET /me -> the signed-in user */
  me() {
    return request({ url: '/me' });
  },

  /** POST /auth/logout - best effort; the local session is cleared regardless. */
  async logout() {
    const refreshToken = tokenStore.getRefreshToken();
    if (refreshToken) {
      try {
        await apiClient.post('/auth/logout', { refresh_token: refreshToken });
      } catch {
        // An expired or already-revoked token still means "signed out" here.
      }
    }
    tokenStore.clear();
    announce('signed-out');
  },

  changePassword({ currentPassword, newPassword }) {
    return request({
      url: '/me/change-password',
      method: 'POST',
      data: { current_password: currentPassword, new_password: newPassword },
    });
  },

  /** POST /auth/verify-email - public; the token from the emailed link. */
  verifyEmail(token) {
    return request({
      url: '/auth/verify-email',
      method: 'POST',
      data: { token },
      skipAuthRefresh: true,
    });
  },

  /** POST /me/verify-email/resend - at most once a minute per account. */
  resendVerification() {
    return request({ url: '/me/verify-email/resend', method: 'POST' });
  },

  listSessions() {
    return request({ url: '/me/sessions' });
  },
};
